import { useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { requestPasswordReset, resetPassword } from '../api/auth'
import { TokenActionCard } from '../components/TokenActionCard'
import { Button } from '../components/ui/Button'
import { Field, Input } from '../components/ui/Field'
import { useT } from '../i18n/context'
import { errText } from '../lib/errors'
import { toast } from '../stores/toast'

export function ResetPasswordPage() {
    const [params] = useSearchParams()
    const token = params.get('token') ?? ''

    if (token) return <ResetForm token={token} />
    return <RequestForm />
}

/** Ohne Token: Reset-Mail anfordern (immer 204, Anti-Enumeration). */
function RequestForm() {
    const t = useT()
    const [email, setEmail] = useState('')
    const [busy, setBusy] = useState(false)
    const [sent, setSent] = useState(false)

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        setBusy(true)
        try {
            await requestPasswordReset(email.trim())
            setSent(true)
        } catch (err) {
            toast.error(errText(err))
        } finally {
            setBusy(false)
        }
    }

    return (
        <TokenActionCard title={t('reset.title')}>
            {sent ? (
                <p>{t('reset.sent')}</p>
            ) : (
                <form onSubmit={submit} className='space-y-3 text-left'>
                    <Field label={t('login.email')}>
                        <Input
                            type='email'
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                            autoComplete='email'
                        />
                    </Field>
                    <Button type='submit' loading={busy} className='w-full'>
                        {t('reset.send')}
                    </Button>
                </form>
            )}
            <Link
                to='/login'
                className='inline-block text-xs font-medium text-indigo-700 dark:text-indigo-400 hover:underline'
            >
                {t('reset.backToLogin')}
            </Link>
        </TokenActionCard>
    )
}

/** Mit Token: neues Passwort setzen. */
function ResetForm({ token }: { token: string }) {
    const t = useT()
    const [email, setEmail] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [repeated, setRepeated] = useState('')
    const [busy, setBusy] = useState(false)
    const [done, setDone] = useState(false)
    const [error, setError] = useState<string | null>(null)

    if (done) return <Navigate to='/login' replace />

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (newPassword !== repeated) {
            setError(t('reset.mismatch'))
            return
        }
        setError(null)
        setBusy(true)
        try {
            await resetPassword({
                email: email.trim(),
                resetPasswordToken: token,
                newPassword,
                repeatedPassword: repeated,
            })
            toast.success(t('reset.savedToast'))
            setDone(true)
        } catch (err) {
            setError(errText(err))
            setBusy(false)
        }
    }

    return (
        <TokenActionCard title={t('reset.newTitle')}>
            <form onSubmit={submit} className='space-y-3 text-left'>
                <Field label={t('login.email')}>
                    <Input
                        type='email'
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                        autoComplete='email'
                    />
                </Field>
                <Field label={t('settings.newPassword')}>
                    <Input
                        type='password'
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        required
                        minLength={8}
                        maxLength={128}
                        autoComplete='new-password'
                    />
                </Field>
                <Field label={t('settings.newPasswordRepeat')}>
                    <Input
                        type='password'
                        value={repeated}
                        onChange={e => setRepeated(e.target.value)}
                        required
                        minLength={8}
                        maxLength={128}
                        autoComplete='new-password'
                    />
                </Field>
                <p className='text-xs text-slate-500 dark:text-slate-400'>{t('reset.hint')}</p>
                {error && (
                    <p
                        role='alert'
                        className='rounded-lg bg-red-50 dark:bg-red-950/60 px-3 py-2 text-sm text-red-700 dark:text-red-300'
                    >
                        {error}
                    </p>
                )}
                <Button type='submit' loading={busy} className='w-full'>
                    {t('reset.save')}
                </Button>
            </form>
        </TokenActionCard>
    )
}
